/**
 * Central auth token manager + axios interceptor.
 *
 * Feature summary:
 *  - Stores access_token + refresh_token per role in localStorage
 *    (keys:  <role>AccessToken / <role>RefreshToken — legacy `<role>Token` preserved
 *    for backward-compat during migration).
 *  - Installs a single global axios response interceptor: on 401, attempts to
 *    refresh the access token using the stored refresh token, then retries the
 *    original request. Concurrent 401s coalesce to a single refresh promise.
 *  - On refresh failure (expired/revoked refresh token) the role is logged out
 *    and user is redirected to the matching login page.
 *  - Exposes saveTokens / clearTokens / logout / logoutAll helpers used by
 *    every login + logout flow.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Role → storage key prefix mapping.
// Order matters — when detecting "current role" we prefer the most privileged.
const ROLES = [
  { name: 'admin',   legacy: 'adminToken',   access: 'adminAccessToken',   refresh: 'adminRefreshToken',   loginPath: '/admin/login' },
  { name: 'partner', legacy: 'partnerToken', access: 'partnerAccessToken', refresh: 'partnerRefreshToken', loginPath: '/' },
  { name: 'client',  legacy: 'clientToken',  access: 'clientAccessToken',  refresh: 'clientRefreshToken',  loginPath: null /* varies by subdomain */ },
  { name: 'trusted', legacy: 'trustedToken', access: 'trustedAccessToken', refresh: 'trustedRefreshToken', loginPath: '/trusted/login' },
];

const ROLE_BY_NAME = Object.fromEntries(ROLES.map(r => [r.name, r]));

/**
 * Persist a (access, refresh) pair for the given role.
 * Also writes the legacy `<role>Token` key for backward compat.
 */
export function saveTokens(role, accessToken, refreshToken) {
  const spec = ROLE_BY_NAME[role];
  if (!spec) {
    console.warn(`saveTokens: unknown role "${role}"`);
    return;
  }
  if (accessToken) {
    localStorage.setItem(spec.access, accessToken);
    localStorage.setItem(spec.legacy, accessToken);   // backward compat
  }
  if (refreshToken) {
    localStorage.setItem(spec.refresh, refreshToken);
  }
}

/**
 * Drop every token for the given role (used on logout/refresh-failure).
 */
export function clearTokens(role) {
  const spec = ROLE_BY_NAME[role];
  if (!spec) return;
  localStorage.removeItem(spec.legacy);
  localStorage.removeItem(spec.access);
  localStorage.removeItem(spec.refresh);
}

/**
 * Wipe the client *identity* keys (id/name/email/subdomain/embed flag) that
 * live outside the token storage. Must be called alongside clearTokens('client')
 * whenever a client session ends or is found invalid, otherwise a leftover
 * identity can re-seed a stale dashboard on a shared browser (ghost wills).
 * The remembered-email key is intentionally preserved for the "Welcome back" UX.
 */
export function clearClientIdentity() {
  localStorage.removeItem('clientId');
  localStorage.removeItem('clientName');
  localStorage.removeItem('clientEmail');
  localStorage.removeItem('clientSubdomain');
  localStorage.removeItem('isEmbedded');
}

export function getAccessToken(role) {
  const spec = ROLE_BY_NAME[role];
  if (!spec) return null;
  return localStorage.getItem(spec.access) || localStorage.getItem(spec.legacy);
}

export function getRefreshToken(role) {
  const spec = ROLE_BY_NAME[role];
  if (!spec) return null;
  return localStorage.getItem(spec.refresh);
}

/**
 * Inspect the Authorization header of a request and figure out which role's
 * token it is, by matching the bearer string against stored values.
 */
function detectRoleFromHeader(bearerToken) {
  if (!bearerToken) return null;
  for (const r of ROLES) {
    const access = localStorage.getItem(r.access);
    const legacy = localStorage.getItem(r.legacy);
    if (access === bearerToken || legacy === bearerToken) return r.name;
  }
  return null;
}

// ---- Refresh orchestration (coalesce concurrent 401s) ----
const refreshPromises = {};    // role -> Promise<newAccessToken>

async function performRefresh(role) {
  if (refreshPromises[role]) return refreshPromises[role];

  refreshPromises[role] = (async () => {
    const refresh = getRefreshToken(role);
    if (!refresh) throw new Error('no-refresh-token');
    const resp = await axios.post(`${API}/auth/refresh`, { refresh_token: refresh });
    const { access_token, refresh_token } = resp.data || {};
    if (!access_token || !refresh_token) {
      throw new Error('malformed-refresh-response');
    }
    saveTokens(role, access_token, refresh_token);
    return access_token;
  })()
    .finally(() => {
      // Allow subsequent refreshes once this one resolves/rejects.
      delete refreshPromises[role];
    });

  return refreshPromises[role];
}

/**
 * Explicit logout — calls the server to revoke the session then clears
 * local storage + navigates to the role's login page.
 */
export async function logout(role) {
  const spec = ROLE_BY_NAME[role];
  const access = getAccessToken(role);
  if (access) {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${access}` } });
    } catch (_) { /* ignore - logout is idempotent */ }
  }
  clearTokens(role);
  if (spec?.loginPath) {
    window.location.href = spec.loginPath;
  }
}

/**
 * Kill every active session for the user on the server. Used by the
 * "Sign out everywhere" button.
 */
export async function logoutAll(role) {
  const access = getAccessToken(role);
  if (!access) throw new Error('not-authenticated');
  const resp = await axios.post(
    `${API}/auth/logout-all`,
    {},
    { headers: { Authorization: `Bearer ${access}` } },
  );
  clearTokens(role);
  return resp.data;
}

export async function listSessions(role) {
  const access = getAccessToken(role);
  if (!access) throw new Error('not-authenticated');
  const resp = await axios.get(`${API}/auth/sessions`, {
    headers: { Authorization: `Bearer ${access}` },
  });
  return resp.data?.sessions || [];
}

// ---------------------------------------------------------------------------
// Global axios response interceptor
// ---------------------------------------------------------------------------
let interceptorInstalled = false;

export function installAuthInterceptor() {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  axios.interceptors.response.use(
    (r) => r,
    async (error) => {
      const originalRequest = error?.config;
      const status = error?.response?.status;

      // Do not try to refresh the refresh-call itself, or unauthenticated calls,
      // or if we've already retried once.
      if (
        !originalRequest
        || status !== 401
        || originalRequest._retriedWithRefresh
        || (originalRequest.url || '').includes('/auth/refresh')
        || (originalRequest.url || '').includes('/auth/logout')
      ) {
        return Promise.reject(error);
      }

      const authHeader = originalRequest.headers?.Authorization || originalRequest.headers?.authorization;
      if (!authHeader || !authHeader.startsWith('Bearer ')) {
        return Promise.reject(error);
      }
      const presentedToken = authHeader.slice(7);
      const role = detectRoleFromHeader(presentedToken);
      if (!role || !getRefreshToken(role)) {
        return Promise.reject(error);
      }

      try {
        const newAccess = await performRefresh(role);
        originalRequest._retriedWithRefresh = true;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return axios(originalRequest);
      } catch (refreshErr) {
        // Refresh failed: session is gone. Wipe tokens (and, for clients, the
        // identity keys too) and redirect to login so the user can never be
        // left staring at a stale dashboard rendered from leftover state.
        const wasEmbedded = role === 'client'
          && typeof window !== 'undefined'
          && localStorage.getItem('isEmbedded') === 'true';
        clearTokens(role);
        if (role === 'client') clearClientIdentity();
        // Resolve the redirect target. Client login lives at /login on the
        // partner subdomain; embedded portals re-auth inside their own iframe
        // page, so a hard redirect would break the embed — skip it there.
        const loginPath = role === 'client'
          ? (wasEmbedded ? null : '/login')
          : (ROLE_BY_NAME[role]?.loginPath || null);
        if (loginPath && typeof window !== 'undefined' && window.location.pathname !== loginPath) {
          window.location.href = loginPath;
        }
        return Promise.reject(error);
      }
    },
  );
}
